// @ts-ignore
import type { FetchOptions } from 'ofetch';
// @ts-ignore
import { ofetch } from 'ofetch';

export default defineNuxtPlugin({
  name: 'app',
  enforce: 'default',
  parallel: true,
  async setup(nuxtApp) {
    const config = useRuntimeConfig()
    const auth = useAuthStore()
    const toast = useToast();
    const settingsStore = useSettingsStore();

    // Define and install $appState
    const $appState = reactive({
      preset: 'Aura',
      primary: 'noir',
      surface: null,
      darkTheme: false,
      codeSandbox: false,
      sourceType: 'options-api',
      announcement: null,
      storageKey: 'primevue'
    });

    nuxtApp.provide('appState', $appState);

    nuxtApp.provide('storage', (path: unknown): string => {
      if (typeof path !== 'string' || !path.trim()) {
        return ''
      }
      return path.startsWith('http://') || path.startsWith('https://')
        ? path
        : config.public.storageBase + path
    });

    function buildHeaders(headers = <HeadersInit>{}): HeadersInit {

      const currentLocale =
        (nuxtApp.$i18n as { locale: { value: string } })?.locale.value || "en";

      // Create the base headers
      let baseHeaders: HeadersInit = {
        'Accept': 'application/json',
      };

      // Cast config.public.appKey to string or provide a default value
      baseHeaders['App-Key'] = (config.public.appKey as string) || 'auth';

      // Add the current language code to the headers
      baseHeaders['App-Locale'] = currentLocale;

      return {
        ...headers,
        ...baseHeaders,
        ...(
          import.meta.server ? {
            'referer': useRequestURL().toString(),
            ...useRequestHeaders(['x-forwarded-for', 'user-agent', 'referer']),
          } : {}
        ),
        ...(
          auth.logged ? {
            'Authorization': `Bearer ${auth.token}`
          } : {}
        )
      };
    }

    function buildBaseURL(baseURL: string): string {
      if (baseURL) return baseURL;

      return import.meta.server ?
        config.apiLocal + config.public.apiPrefix
        : config.public.apiBase + config.public.apiPrefix;
    }

    function buildSecureMethod(options: FetchOptions): void {
      if (import.meta.server) return;

      const method = options.method?.toLowerCase() ?? 'get'

      if (options.body instanceof FormData && method === 'put') {
        options.method = 'POST';
        options.body.append('_method', 'PUT');
      }
    }

    function isRequestWithAuth(baseURL: string, path: string): boolean {
      return !baseURL
        && !path.startsWith('/_nuxt')
        && !path.startsWith('http://')
        && !path.startsWith('https://');
    }

    async function initializeSanctum() {
      try {
        // Make a call to `sanctum/csrf-cookie` to initialize Sanctum
        await $fetch('/sanctum/csrf-cookie', {
          baseURL: config.public.apiBase,
          credentials: 'include', // Include cookies in the request
        });
      } catch (error) {
        console.error('Failed to initialize Sanctum:', error);
      }
    }

    globalThis.$fetch = ofetch.create(<FetchOptions>{
      retry: false,

      onRequest({ request, options }) {
        if (!isRequestWithAuth(options.baseURL ?? '', request.toString())) return

        options.credentials = 'include';

        options.baseURL = buildBaseURL(options.baseURL ?? '');
        options.headers = buildHeaders(options.headers);

        buildSecureMethod(options);
      },

      onRequestError({ error }) {
        if (import.meta.server) return;

        if (error.name === 'AbortError') return;

/*        toast.add({
          severity: "error",
          summary: error.message ?? 'Something went wrong',
          life: 3000,
        });*/
      },

      onResponseError({ response }) {
        if (response.status === 401) {
          if (auth.logged) {
            auth.token = ''
            auth.user = <User>{}
          }

          if (import.meta.client) {
            toast.add({
              severity: 'warn',
              summary: 'Please log in to continue',
              life: 3000,
            });
          }
        } else if (response.status !== 422) {
          if (import.meta.client) {
/*            toast.add({
              severity: 'error',
              summary: response._data?.message ?? response.statusText ?? 'Something went wrong',
              life: 3000,
            });*/
          }
        }
      }
    })

    // Initialize Sanctum before fetching settings or user data
    await initializeSanctum();

    // Fetch public settings on boot
    await settingsStore.fetchPublicSettings();

    if (auth.logged) {
      await auth.fetchUser();
      await settingsStore.fetchAuthSettings();
    }

    // Watch for login state changes and fetch auth settings on login
/*    watch(
      () => auth.logged,
      async (loggedIn) => {
        if (loggedIn) {
          await settingsStore.fetchAuthSettings();
        }
      }
    );*/
  },
})
